import { EditableProTable } from '@ant-design/pro-components'
import { Button, Form, Popconfirm, Select, Typography, Upload, Modal } from 'antd'
import FormSection from 'components/form-section'
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { generateUuid } from 'utils/dataUtil'
import { isDecreasing, isIncreasing } from 'utils/number'
import Papa from 'papaparse'
import { TableOutlined } from '@ant-design/icons'
import SWTableEditor from './SWTableEditor'

import classes from './Table.module.scss'
import { isNumeric } from 'utils/string'
import { useQuery } from '@tanstack/react-query'
import { getReferenceList, getReferenceData } from 'service/completionOptimizer'

const SWTable = ({ tab, fields = [], operation, isCondensate }) => {
  const form = Form.useFormInstance()

  const [data, setData] = useState([])
  const [errors, setErrors] = useState([])
  const [isSWTableEditorOpen, setIsSWTableEditorOpen] = useState(false);
  const [editingData, setEditingData] = useState([]);
  const tableEditorRef = useRef(null);

  const { data: references } = useQuery({
    queryKey: ['references', tab === 'matrix' ? 'scal' : 'fracture', 'swo'],
    queryFn: () => {
      console.log(`Fetching ${tab} SWO references list...`)
      return getReferenceList(tab === 'matrix' ? 'scal' : 'fracture', 'swo').then(data => {
        console.log('SWO Reference List:', {
          group: tab === 'matrix' ? 'scal' : 'fracture',
          type: 'swo',
          data: data
        })
        return data
      })
    }
  })

  const referenceOptions = useMemo(() => {
    if (!references) return []
    return references.map(ref => ({
      label: ref.name,
      value: ref.id
    }))
  }, [references])

  const handleReferenceChange = async (value) => {
    if (!value) return

    try {
      const group = tab === 'matrix' ? 'scal' : 'fracture'
      console.log('Fetching SWO reference data:', {
        group,
        type: 'swo',
        id: value
      })
      
      const referenceData = await getReferenceData(group, 'swo', value)
      console.log('SWO Reference Data:', {
        group,
        type: 'swo',
        id: value,
        data: referenceData
      })

      // Remove existing fields
      const fieldCount = fields.length
      for (let i = fieldCount - 1; i >= 0; i--) {
        operation?.remove?.(i)
      }

      // Convert object to array and filter out length property
      const dataArray = Object.entries(referenceData)
        .filter(([key]) => key !== 'length')
        .map(([_, value]) => value)

      // Add reference data rows
      dataArray.forEach(row => {
        const newRow = {
          SW: row.sw,
          KRW: row.krw,
          KRO: row.kro,
          PSIA: row.psia
        }
        operation?.add(newRow)
      })
    } catch (error) {
      console.error('Error loading reference data:', error)
    }
  }

  const fetchData = () => {
    setData(fields.map((field, index) => ({
      ...form.getFieldValue(["SCAL", tab, "SWO", field.name]),
      key: index
    })) ?? [])
  }

  useEffect(() => {
    validateData(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields])

  const columns = useMemo(
    () => {
      const baseColumns = [
        {
          key: 'key',
          dataIndex: 'key',
          title: "Row",
          editable: false,
          render: (dom, record, index) => index + 1
        },
        {
          key: 'SW',
          dataIndex: 'SW',
          title: 'Sw',
          editable: true,
        },
        {
          key: 'KRW',
          dataIndex: 'KRW',
          title: 'Krw',
          editable: (text, record, index) => index !== 0,
        }
      ]

      // Add KRO column only if not condensate
      if (!isCondensate) {
        baseColumns.push({
          key: 'KRO',
          dataIndex: 'KRO',
          title: 'Kro',
          editable: true,
        })
      }

      // Add PSIA column
      baseColumns.push({
        key: 'PSIA',
        dataIndex: 'PSIA',
        title: 'Pc (psia)',
        editable: true,
      })

      // Add operation column
      baseColumns.push({
        title: "Operation",
        valueType: "option",
        key: "operation",
        width: 160,
        render: (text, record, index, action) => [
          <Button
            key="editable"
            type="text"
            className="text-primary"
            onClick={() => {
              action?.startEditable?.(record.key);
            }}
          >
            Edit
          </Button>,
          <Popconfirm
            key="delete"
            title="Sure to delete?"
            onConfirm={() => {
              operation?.remove?.(index);
              const currentSWData = form.getFieldValue(["SCAL", tab, "SWO"]);
              const newSWData = currentSWData.filter((_, i) => i !== index);
              form.setFieldValue(["SCAL", tab, "SWO"], newSWData);
              fetchData();
            }}
          >
            <Button type="text" danger>Delete</Button>
          </Popconfirm>
        ],
      })

      return baseColumns
    }, [operation, isCondensate])

  const validateData = useCallback((_data = []) => {
    setErrors([])

    const _errors = []

    if (!_data.length) return;

    const swValues = []
    const krwValues = []
    const kroValues = []
    const pcValues = []

    _data.forEach((row, index) => {
      if (!isNumeric(row.SW)) {
        _errors.push(`Value of Sw in row #${index + 1} must be a number`)
      } else {
        const sw = parseFloat(row.SW)
        if (sw < 0 || sw > 1) {
          _errors.push(`Value of Sw in row #${index + 1} must be between 0 and 1`)
        } else swValues.push(sw)
      }
      if (!isNumeric(row.KRW)) {
        _errors.push(`Value of Krw in row #${index + 1} must be a number`)
      } else {
        const krw = parseFloat(row.KRW)
        if (krw < 0 || krw > 1) {
          _errors.push(`Value of Krw in row #${index + 1} must be between 0 and 1`)
        } else krwValues.push(krw)
      }
      if (!isCondensate) {
        if (!isNumeric(row.KRO)) {
          _errors.push(`Value of Kro in row #${index + 1} must be a number`)
        } else {
          const kro = parseFloat(row.KRO)
          if (kro < 0 || kro > 1) {
            _errors.push(`Value of Kro in row #${index + 1} must be between 0 and 1`)
          } else kroValues.push(kro)
        }
      }
      if (!isNumeric(row.PSIA)) {
        _errors.push(`Value of Pc (psia) in row #${index + 1} must be a number`)
      } else {
        const pc = parseFloat(row.PSIA)
        pcValues.push(pc)
      }
    })

    if (_errors.length) {
      setErrors(_errors)
      return
    }

    if (!isCondensate) {
      const lastKroValue = kroValues[kroValues.length - 1]
      if (kroValues.length > 1 && lastKroValue !== 0) {
        _errors.push(`The last Kro value must be 0`)
      }

      const isKroDecreasing = isDecreasing(kroValues)
      if (!isKroDecreasing) {
        _errors.push(`The values of Kro must be decreasing`)
      }
    }

    const isSwIncreasing = isIncreasing(swValues)
    if (!isSwIncreasing) {
      _errors.push(`The values of Sw must be increasing`)
    }

    const isKrwIncreasing = isIncreasing(krwValues)
    if (!isKrwIncreasing) {
      _errors.push(`The values of Krw must be increasing`)
    }

    const isPcDecreasing = isDecreasing(pcValues)
    if (!isPcDecreasing) {
      _errors.push(`The values of Pc (psia) must be level or decreasing`)
    }

    setErrors(_errors)
  }, [isCondensate])

  const handleFileUpload = (file) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      complete: (result) => {
        const parsedData = result.data
          .filter(row => Object.values(row).some(value => value !== null))
          .map((row, index) => ({
            ...row,
            key: generateUuid(),
          }));

        setData(parsedData);

        fields.forEach((field, index) => {
          if (parsedData[index]) {
            const recordData = {
              SW: parsedData[index].SW,
              KRW: parsedData[index].KRW,
              KRO: parsedData[index].KRO,
              PSIA: parsedData[index].PSIA
            };
            form.setFieldValue(["SCAL", tab, "SWO", field.name], recordData);
          }
        });

        validateData(parsedData);
      },
    });
    return false;
  };

  const handleSWTableEditorOpen = () => {
    setEditingData(data);
    setIsSWTableEditorOpen(true);
  };

  const handleSWTableEditorCancel = () => {
    setEditingData(data);
    setIsSWTableEditorOpen(false);
  };

  const handleSWTableEditorSave = () => {
    const currentEditingData = tableEditorRef.current?.getCurrentData() || [];

    // Remove existing fields
    const fieldCount = fields.length;
    for (let i = fieldCount - 1; i >= 0; i--) {
      operation?.remove?.(i);
    }

    // Add all rows from editing data
    currentEditingData.forEach((row) => {
      const newRow = {
        SW: row.SW,
        KRW: row.KRW,
        KRO: row.KRO,
        PSIA: row.PSIA
      };
      operation?.add?.(newRow);
    });

    setData(currentEditingData);
    setIsSWTableEditorOpen(false);
    fetchData();
  };

  // Add title generator based on columns
  const tableTitle = useMemo(() => {
    if (isCondensate) {
      return "SWF (Sw-Krw-Pc)" // Without Kro for condensate
    }
    return "SWO (Sw-Krw-Kro-Pc)" // With Kro for non-condensate
  }, [isCondensate])

  return (
    <FormSection
      title={tableTitle}
      extra={
        <div className="d-flex justify-content-end align-items-center gap-2">
          <Button 
            icon={<TableOutlined />}
            onClick={handleSWTableEditorOpen}
            title="Open Table Editor"
          />
          <Upload
            accept=".csv"
            showUploadList={false}
            beforeUpload={handleFileUpload}
          >
            <Button type="primary">Upload CSV</Button>
          </Upload>
          <Typography.Text className="text-light">Reference</Typography.Text>
          <Form.Item 
            name={['SCAL', tab, 'SWO', 'reference']}
            noStyle
          >
            <Select
              style={{ minWidth: 120 }} 
              dropdownMatchSelectWidth={false}
              options={referenceOptions}
              onChange={handleReferenceChange}
              allowClear
            />
          </Form.Item>
        </div>
      }
      bodyStyle={{
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0
      }}
    >
      <EditableProTable
        options={false}
        pagination={false}
        search={false}
        columns={columns}
        value={data}
        rowKey="key"
        tableExtraRender={() => (
          <ul className="px-24 text-danger">
            {errors.map(errMsg => (
              <li key={errMsg}>
                <Typography.Text type="danger">{errMsg}</Typography.Text>
              </li>
            ))}
          </ul>
        )}
        sticky={{
          offsetHeader: 32
        }}
        className={classes.tableContainer}
        cardProps={{
          className: classes.card
        }}
        tableClassName={classes.table}
        editable={{
          type: 'multiple',
          actionRender: (row, config, defaultDom) => [
            defaultDom.save,
            defaultDom.cancel,
          ],
          onSave: (rowKey, data, row) => {
            const { index, isNew, ...recordData } = data
            if (isNew) {
              operation?.add?.(recordData)
            } else {
              form.setFieldValue(["SCAL", tab, "SWO", index], recordData)
              fetchData()
            }
          }
        }}
        recordCreatorProps={{
          record: (index) => ({
            key: generateUuid(),
            isNew: true,
            KRW: index === 0 ? 0 : null
          })
        }}
      />
      <Modal
        title="Table Editor"
        open={isSWTableEditorOpen}
        onCancel={handleSWTableEditorCancel}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ 
          padding: '12px',
          height: 'calc(80vh - 108px)',
          overflow: 'hidden'
        }}
        footer={[
          <Button key="cancel" onClick={handleSWTableEditorCancel}>
            Cancel
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            onClick={handleSWTableEditorSave}
          >
            Save
          </Button>
        ]}
      >
        <div style={{ height: '100%' }}>
          <SWTableEditor
            ref={tableEditorRef}
            data={editingData}
            columns={columns}
            onDataChange={setEditingData}
          />
        </div>
      </Modal>
    </FormSection>
  )
}

export default SWTable